import type {ReactElement, ReactNode} from 'react';
import {useState, useRef} from 'react';
import React from 'react';
import type {ResponseFeedbackProps} from './response-feedback.types';
import {FeedbackType, MessageRole} from '@Components/ai-chat-window/ai-chat-window.types';
import {CustomFeedbackIcon} from '@Components/ai-chat-window/components/custom-feedback-icon/custom-feedback-icon';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {isTouchDevice} from '@Utils/browser.util';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {recordFeedback} from '@Libraries/ai-chat.library';
import {aiChatWindowActions} from '@Components/ai-chat-window/ai-chat-window.reducer';
import {ChatImageActionPanelId} from '@Components/ai-chat-window/components/generated-image-actions-icons/generated-image-actions-icons.types';
import {useAppDispatch} from '@/hooks';
import {FeedbackPopup} from '@Components/ai-chat-window/components/feedback-popup/feedback-popup';
import {useChangeStyleThemeSelect} from '@Components/ai-chat-window/hooks/useChangeStyleThemeSelect';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import styles from './response-feedback.module.scss';
import type {AIChatNegativeFeedbackTypes} from '@Components/ai-chat-window/components/feedback-popup/feedback-popup.types';

export function ResponseFeedback({dropdownPopupAlignment = ALIGNMENT_TYPE.TOP_START, ...props}: ResponseFeedbackProps): ReactNode {
  const dispatch = useAppDispatch();
  const [negativeHoverDisplayActive, setNegativeHoverDisplayActive] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {onThemeSelect: onChangeStyleThemeSelect} = useChangeStyleThemeSelect({
    onGenerate: props.onGenerate,
    imageHashedId: props.imageHashedId,
    imageWidth: props.imageWidth,
    imageHeight: props.imageHeight,
  });

  const showNegativeFeedbackHover = (): void => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setNegativeHoverDisplayActive(true);
  };

  const hideNegativeFeedbackHover = (): void => {
    hoverTimeoutRef.current = setTimeout(() => {
      setNegativeHoverDisplayActive(false);
      hoverTimeoutRef.current = null;
    }, 200);
  };

  const negativeFeedbackPopup = (): ReactElement => {
    const handleTemplateClick = (): void => {
      window.PMW.openGalleryDialog('', true, '', {showEventsWizardBanner: false, shouldUpsellForSemanticTagging: false}, false, true);
      props.onCloseNegativeFeedbackTextInputPopup?.();
    };

    const handleEditInEditorClick = (): void => {
      void props.onOpenInEditor();
      props.onCloseNegativeFeedbackTextInputPopup?.();
    };

    const hasSubmittedNegativeFeedbackWithReason = props.message.feedback?.type === FeedbackType.NEGATIVE && !!props.message.feedback.negativeFeedbackPreset;

    return (
      <FeedbackPopup
        idAiChat={props.chatId}
        messageId={props.message.messageId}
        onSubmitFeedback={onSubmitFeedback}
        isAlreadySubmitted={hasSubmittedNegativeFeedbackWithReason}
        initialNegativeFeedbackPreset={props.message.feedback?.negativeFeedbackPreset}
        onChangeStyleThemeSelect={(theme): void => {
          props.onCloseNegativeFeedbackTextInputPopup?.();
          setNegativeHoverDisplayActive(false);
          onChangeStyleThemeSelect(theme);
        }}
        onClickTemplate={handleTemplateClick}
        onClickEditInEditor={handleEditInEditorClick}
        isMessageStreaming={props.isMessageStreaming}
        canWrite={props.canWrite}
      />
    );
  };

  const handleNegativeFeedbackDropdownOpen = (): void => {
    props.onOpenNegativeFeedbackTextInputPopup?.();
    if (props.message.feedback?.type !== FeedbackType.NEGATIVE) {
      onSubmitFeedback(FeedbackType.NEGATIVE);
    }
  };

  const getNegativeFeedbackComponent = (isSelected: boolean): ReactNode => {
    let selectorComponent: ReactNode;

    if (isSelected) {
      selectorComponent = <CustomFeedbackIcon cursorClassname={'_cursor-pointer'} svg={getThumbsDownIconSvg()} />;
    } else {
      selectorComponent = getNegativeFeedbackIcon(false);
    }

    const dropdownOpen = props.isNegativeFeedbackTextInputPopupOpen || negativeHoverDisplayActive;

    const handleMouseEnter = (): void => {
      if (isSelected) showNegativeFeedbackHover();
    };

    const handleMouseLeave = (): void => {
      if (isSelected) hideNegativeFeedbackHover();
    };

    return (
      <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onFocus={handleMouseEnter} onBlur={handleMouseLeave} className="_fit-width">
        <Dropdown
          open={dropdownOpen}
          key={ChatImageActionPanelId.NEGATIVE_FEEDBACK_DROPDOWN_MODAL_PANEL_ID}
          id={ChatImageActionPanelId.NEGATIVE_FEEDBACK_DROPDOWN_MODAL_PANEL_ID}
          onOpen={handleNegativeFeedbackDropdownOpen}
          onClose={props.onCloseNegativeFeedbackTextInputPopup}
          modalClassName={'spacing-p-l-4 spacing-p-r-4 spacing-p-b-4'}
          doCloseOnInnerClick={false}
          showModalForSmallScreen
          panelIdForModal={ChatImageActionPanelId.NEGATIVE_FEEDBACK_DROPDOWN_MODAL_PANEL_ID}
          position={DROPDOWN_POSITION.ABSOLUTE}
          modalTitle={window.i18next.t('pmwjs_help_us_improve')}
          className={props.dropdownClassname}
          selectorClassNameOnOpen={'-active'}
          selector={selectorComponent as ReactElement}
          popup={negativeFeedbackPopup()}
          alignment={dropdownPopupAlignment}
          popupClassName={props.popupClassname}
          useDefaultArrow
        />
      </div>
    );
  };

  const handleThumbsUp = (): void => {
    onSubmitFeedback(FeedbackType.POSITIVE);
  };

  const onSubmitFeedback = (type: FeedbackType, negativeFeedbackPreset: AIChatNegativeFeedbackTypes | null = null, text: string | null = null): void => {
    if (!props.message.messageId) {
      return;
    }

    void recordFeedback(props.chatId, props.message.messageId, type, negativeFeedbackPreset, text);
    const currentMessageId = props.message.messageId;

    if (!currentMessageId) {
      return;
    }

    const updatedMessages = {...props.messages};

    if (updatedMessages[currentMessageId].role !== MessageRole.ASSISTANT) {
      return;
    }

    updatedMessages[currentMessageId] = {
      ...updatedMessages[currentMessageId],
      feedback: {
        type,
        comment: text,
        negativeFeedbackPreset: negativeFeedbackPreset,
      },
    };

    dispatch(aiChatWindowActions.updateMessage({chatId: props.chatId, messageId: currentMessageId, message: updatedMessages[currentMessageId]}));
  };

  const getThumbsUpIconSvg = (): ReactNode => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 14 14" fill="none">
        <path
          d="M6.30371 0C6.94654 0.000123607 7.56319 0.256547 8.01758 0.710938C8.47183 1.1654 8.72754 1.78249 8.72754 2.4248V4.52539H11.8984C12.1549 4.52289 12.4096 4.57617 12.6436 4.68164C12.8784 4.78708 13.0879 4.94238 13.2568 5.13672C13.4257 5.33112 13.551 5.56027 13.623 5.80762C13.695 6.05506 13.7123 6.31537 13.6738 6.57031L12.7822 12.3887C12.7177 12.8126 12.5018 13.1991 12.1748 13.4766C11.8489 13.7534 11.4342 13.9039 11.0068 13.8994H4.60117C4.38026 13.8994 4.20117 13.7203 4.20117 13.4994V5.14268C4.20117 4.25523 4.42363 3.46134 4.80317 2.65914L5.86035 0.288086C5.93843 0.1133 6.11235 0 6.30371 0ZM0.941406 5.81836L2.83357 5.81917C3.05442 5.81926 3.2334 5.99832 3.2334 6.21917V13.4994C3.2334 13.7203 3.05431 13.8994 2.8334 13.8994H0.976562C0.968763 13.8994 0.959115 13.8989 0.948242 13.8984H0.943359C0.917359 13.8969 0.882773 13.8942 0.842773 13.8887C0.765274 13.8777 0.652672 13.8543 0.53418 13.7998C0.414208 13.7448 0.274955 13.652 0.166992 13.499C0.0561064 13.3421 7.01216e-05 13.1522 0 12.9424V6.74512C6.57804e-05 6.54637 0.0534044 6.3657 0.157227 6.21484C0.258727 6.06784 0.38943 5.97687 0.50293 5.92188C0.614857 5.86794 0.720488 5.84305 0.793945 5.83105C0.831832 5.82508 0.864706 5.82181 0.889648 5.82031C0.901977 5.81933 0.912975 5.81934 0.921875 5.81934L0.933594 5.81836H0.941406Z"
          fill="#00688C"
        />
      </svg>
    );
  };

  const getThumbsDownIconSvg = (): ReactNode => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 14 14" fill="none">
        <path
          d="M6.30371 13.8994C6.94654 13.8993 7.56319 13.6429 8.01758 13.1885C8.47183 12.734 8.72754 12.1169 8.72754 11.4746V9.37402H11.8984C12.1549 9.37652 12.4096 9.32324 12.6436 9.21777C12.8784 9.11234 13.0879 8.95703 13.2568 8.7627C13.4257 8.5683 13.551 8.33915 13.623 8.0918C13.695 7.84436 13.7123 7.58404 13.6738 7.3291L12.7822 1.51074C12.7177 1.08678 12.5018 0.700328 12.1748 0.422852C11.8489 0.146029 11.4342 -0.00447655 11.0068 0H4.60117C4.38026 0 4.20117 0.179086 4.20117 0.4V8.75673C4.20117 9.64418 4.42363 10.4381 4.80317 11.2403L5.86035 13.6113C5.93843 13.7861 6.11235 13.8994 6.30371 13.8994ZM0.941406 8.08105L2.83357 8.08025C3.05442 8.08015 3.2334 7.9011 3.2334 7.68025V0.4C3.2334 0.179086 3.05431 0 2.8334 0H0.976562C0.968763 5.72205e-06 0.959115 0.000551224 0.948242 0.000976562H0.943359C0.917359 0.00247669 0.882773 0.00524235 0.842773 0.0107422C0.765274 0.0217428 0.652672 0.0451136 0.53418 0.0996094C0.414208 0.154617 0.274955 0.247443 0.166992 0.400391C0.0561064 0.557274 7.01216e-05 0.747231 0 0.957031V7.1543C6.57804e-05 7.35304 0.0534044 7.53371 0.157227 7.68457C0.258727 7.83157 0.38943 7.92254 0.50293 7.97754C0.614857 8.03147 0.720488 8.05637 0.793945 8.06836C0.831832 8.07433 0.864706 8.0776 0.889648 8.0791C0.901977 8.08008 0.912975 8.08008 0.921875 8.08008L0.933594 8.08105H0.941406Z"
          fill="#00688C"
        />
      </svg>
    );
  };

  const getPositiveFeedbackComponent = (isSelected: boolean): ReactNode => {
    if (isSelected) {
      return <CustomFeedbackIcon svg={getThumbsUpIconSvg()} />;
    }

    return (
      <OverlayTrigger
        trigger={['hover', 'focus']}
        overlay={<Tooltip className={`spacing-m-b-1 _rounded-arrow ${isTouchDevice() ? '_invisible' : ''}`}>{window.i18next.t('pmwjs_good_response')}</Tooltip>}
        delay={{show: 0, hide: 0}}
        placement="top"
        offset={undefined}
      >
        <Icon
          isDisabled={props.areFeedbackOptionsDisabled}
          icon="icon-thumbs-up"
          size={IconSize.SIZE_ICON_20}
          type={IconType.GHOST}
          shape={IconShape.CIRCLE}
          onClick={handleThumbsUp}
          className={props.isMobile ? styles.mobileIcon : ''}
        />
      </OverlayTrigger>
    );
  };

  const getNegativeFeedbackIcon = (isSelected: boolean): ReactElement => {
    return (
      <Icon
        isDisabled={isSelected || props.areFeedbackOptionsDisabled}
        icon="icon-thumbs-down"
        size={IconSize.SIZE_ICON_20}
        type={IconType.GHOST}
        shape={IconShape.CIRCLE}
        className={props.isMobile ? styles.mobileIcon : ''}
      />
    );
  };

  const getFeedbackIcons = (): ReactNode => {
    const isPositiveSelected = props.message.feedback?.type === FeedbackType.POSITIVE;
    const isNegativeSelected = props.message.feedback?.type === FeedbackType.NEGATIVE;

    return (
      <div className={`flexbox ${props.containerClassName}`}>
        {getPositiveFeedbackComponent(isPositiveSelected)}
        {getNegativeFeedbackComponent(isNegativeSelected)}
      </div>
    );
  };

  return getFeedbackIcons();
}
